import React, { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PasswordInput } from '@/components/common/PasswordInput';
import { changePasswordErrorMessage } from '@/utils/changePasswordErrorMessage';
import type { PasswordResetHandoff } from '@/utils/passwordResetRequired';
import { useT } from '@/i18n/labels';
import { useThemeStore } from '@/stores/themeStore';
import { dirFor } from '@/stores/langStore';
import { makeTokens } from '@/theme/tokens';

const MIN_PASSWORD_LENGTH = 8;

// Same inline link/hint idiom as ForgotPasswordPage — existing classes only.
const linkButtonStyle: React.CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'var(--accent)',
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
  textDecoration: 'underline',
};

const hintStyle: React.CSSProperties = { marginTop: 6, marginBottom: 0, fontSize: 12.5, opacity: 0.75 };

/**
 * Forced temporary-password rotation — PRE-authentication. Same process as the
 * admin portal's ChangePasswordPage: a user provisioned (or reset) with a temporary
 * password types it on the Login page, the backend validates the credentials but
 * answers 403 `password_reset_required` with no token, and LoginPage routes here with
 * { email, currentPassword, tenantId } in router state. The user chooses a new
 * password; on success the backend returns a full token set, which establishes the
 * session and lands the user in the console. Reaching this page without that state
 * (a direct visit, or a refresh — the state is in memory only) goes back to /login.
 */
export const ChangePasswordPage: React.FC = () => {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const { changePassword } = useAuth();
  const mode = useThemeStore((s) => s.mode);
  const tokens = useMemo(() => makeTokens({ mode }), [mode]);

  const resetState = (location.state as PasswordResetHandoff | null) ?? null;

  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // No hand-off credentials → this page is only reachable mid-reset. Back to login.
  if (!resetState?.email || !resetState.currentPassword || !resetState.tenantId) {
    return <Navigate to="/login" replace />;
  }

  /** Client-side validation; returns an error string or '' when valid. The backend
   *  enforces the full password policy and answers 400 with a message if the new
   *  password is too weak — that message is shown on submit. */
  const validate = (): string => {
    if (next.length < MIN_PASSWORD_LENGTH) {
      return t('changePwMinLength', { n: MIN_PASSWORD_LENGTH });
    }
    if (next !== confirm) {
      return t('changePwMismatch');
    }
    if (next === resetState.currentPassword) {
      return t('changePwSameAsTemp');
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      await changePassword(resetState.email, resetState.currentPassword, next, resetState.tenantId);
      navigate('/', { replace: true });
    } catch (err) {
      setError(changePasswordErrorMessage(err, lang));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ac-login" dir={dirFor(lang)} style={tokens as React.CSSProperties}>
      <form onSubmit={handleSubmit} className="ac-card ac-login-card" data-testid="change-password-form">
        <div className="ac-login-title">{t('changePwTitle')}</div>
        <div className="ac-login-sub">{t('changePwSubtitle')}</div>

        {error && (
          <div role="alert" dir="auto" className="ac-login-err">
            {error}
          </div>
        )}

        <div className="ac-set-field-group" style={{ marginBottom: 14 }}>
          <label htmlFor="changePwAccount" className="ac-set-field-label">
            {t('changePwAccount')}
          </label>
          <input
            id="changePwAccount"
            type="email"
            className="ac-field"
            value={resetState.email}
            readOnly
          />
        </div>

        <div className="ac-set-field-group" style={{ marginBottom: 14 }}>
          <label htmlFor="changePwNew" className="ac-set-field-label">
            {t('changePwNewPassword')}
          </label>
          <PasswordInput
            id="changePwNew"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            autoComplete="new-password"
          />
          <p style={hintStyle} dir="auto" data-testid="change-password-rules">
            {t('changePwRules')}
          </p>
        </div>

        <div className="ac-set-field-group" style={{ marginBottom: 22 }}>
          <label htmlFor="changePwConfirm" className="ac-set-field-label">
            {t('changePwConfirmPassword')}
          </label>
          <PasswordInput
            id="changePwConfirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            autoComplete="new-password"
          />
        </div>

        <button type="submit" disabled={loading} className="ac-btn primary" style={{ width: '100%' }}>
          {loading ? t('changePwSaving') : t('changePwSubmit')}
        </button>

        <p style={{ marginTop: 14, marginBottom: 0, textAlign: 'center' }}>
          <button type="button" onClick={() => navigate('/login')} style={linkButtonStyle}>
            {t('changePwBackToLogin')}
          </button>
        </p>
      </form>
    </div>
  );
};

export default ChangePasswordPage;
